import { openStationDb, req, transact } from './db';

/** Ein noch nicht übertragener Rückgabe-Scan (HLP-06: Stationen funktionieren auch offline). */
export interface QueuedScan {
  id: string;
  code: string;
  undo: boolean;
  createdAt: number;
  /** Nur zum Sortieren innerhalb derselben Sitzung: `createdAt` allein kann bei sehr schnellem Scannen gleich sein. */
  seq: number;
  attempts: number;
  /**
   * Korb-Modus (O4): der gewählte Korb. Optionales Feld, keine neue DB-Version: Einträge alter Form (ohne das Feld)
   * werden als gewöhnlicher Einzelscan weiter verarbeitet.
   */
  expectedRegistrationId?: string;
  /**
   * Die Person, die den Scan erfasst hat (Kennung des Kontos). Der Scan geht nur unter ihrem Konto hinaus, nie unter dem der
   * nächsten Person am Gerät. Optionales Feld, keine neue DB-Version: Einträge alter Form (ohne das Feld) gehen unter jedem Konto.
   */
  userId?: string;
}

/** So oft wird ein Scan wiederholt, bevor er als nicht übertragbar gilt und aus der Warteschlange fällt. */
export const MAX_SCAN_ATTEMPTS = 50;

/** Gehört der Scan einer anderen Person (Einträge ohne Person gehören niemandem und gehen unter jedem Konto hinaus)? */
export const isOthers = (scan: QueuedScan, person: string | null): boolean =>
  scan.userId !== undefined && scan.userId !== person;

/**
 * Welche Scans bleiben liegen? Die einer anderen Person, und alle späteren Scans derselben Etikette: Sie dürfen nicht an dem
 * wartenden Scan der anderen Person vorbeiziehen (Reihenfolge je Code, z. B. Rückgabe von A, dann „Rückgängig“ von B).
 */
export function heldScanIds(scans: readonly QueuedScan[], person: string | null): Set<string> {
  const held = new Set<string>();
  const heldCodes = new Set<string>();
  for (const scan of scans) {
    if (isOthers(scan, person)) {
      held.add(scan.id);
      heldCodes.add(scan.code);
    } else if (heldCodes.has(scan.code)) {
      held.add(scan.id);
    }
  }
  return held;
}

export class StationStore {
  private sequence = 0;

  private constructor(private readonly db: IDBDatabase) {}

  static async open(name: string): Promise<StationStore> {
    return new StationStore(await openStationDb(name));
  }

  close(): void {
    this.db.close();
  }

  async getMeta<T>(key: string): Promise<T | undefined> {
    return transact(this.db, ['meta'], 'readonly', async ({ meta }) => {
      return (await req(meta.get(key))) as T | undefined;
    });
  }

  async setMeta(key: string, value: unknown): Promise<void> {
    await transact(this.db, ['meta'], 'readwrite', async ({ meta }) => {
      await req(meta.put(value, key));
    });
  }

  async deleteMeta(key: string): Promise<void> {
    await transact(this.db, ['meta'], 'readwrite', async ({ meta }) => {
      await req(meta.delete(key));
    });
  }

  async queueScan(
    code: string,
    undo: boolean,
    newId: () => string = () => crypto.randomUUID(),
    expectedRegistrationId?: string,
    userId?: string,
  ) {
    const scan: QueuedScan = {
      id: newId(),
      code,
      undo,
      createdAt: Date.now(),
      seq: this.sequence++,
      attempts: 0,
      ...(expectedRegistrationId === undefined ? {} : { expectedRegistrationId }),
      ...(userId === undefined ? {} : { userId }),
    };
    await transact(this.db, ['outbox'], 'readwrite', async ({ outbox }) => {
      await req(outbox.add(scan));
    });
    return scan;
  }

  async pending(): Promise<number> {
    return transact(this.db, ['outbox'], 'readonly', async ({ outbox }) => req(outbox.count()));
  }

  /** Wartende Scans, die einer anderen Person gehören als `person` (`null`: niemand angemeldet). Sie gehen unter `person` nie hinaus. */
  async pendingOfOthers(person: string | null): Promise<number> {
    return heldScanIds(await this.all(), person).size;
  }

  /** Wartet schon ein Scan einer anderen Person für diesen Code (der neue Scan muss sich dann hinten anreihen)? */
  async holdsCode(code: string, person: string | null): Promise<boolean> {
    return (await this.all()).some((scan) => scan.code === code && isOthers(scan, person));
  }

  /** In der Reihenfolge des Scannens (`id` ist ein Zufalls-UUID, `getAll()` allein sortiert also nicht danach). */
  async all(): Promise<QueuedScan[]> {
    return transact(this.db, ['outbox'], 'readonly', async ({ outbox }) => {
      const scans = (await req(outbox.getAll())) as QueuedScan[];
      return [...scans].sort((a, b) => a.createdAt - b.createdAt || a.seq - b.seq);
    });
  }

  async markDone(id: string): Promise<void> {
    await transact(this.db, ['outbox'], 'readwrite', async ({ outbox }) => {
      await req(outbox.delete(id));
    });
  }

  /** Zählt einen Fehlversuch. Gibt `'dropped'` zurück, wenn die Grenze erreicht ist (dann ist der Eintrag entfernt). */
  async markAttempt(id: string): Promise<'retry' | 'dropped'> {
    return transact(this.db, ['outbox'], 'readwrite', async ({ outbox }) => {
      const current = (await req(outbox.get(id))) as QueuedScan | undefined;
      if (current === undefined) return 'dropped';
      const attempts = current.attempts + 1;
      if (attempts >= MAX_SCAN_ATTEMPTS) {
        await req(outbox.delete(id));
        return 'dropped';
      }
      await req(outbox.put({ ...current, attempts }));
      return 'retry';
    });
  }

  /**
   * Löscht alle lokalen Daten, aber nur, wenn die Warteschlange wirklich leer ist (HLP-06: nach dem Ende des
   * Basars, sobald alles übertragen ist) – geprüft und geleert in derselben Transaktion, damit ein Scan, der
   * gerade nebenbei eingereiht wird, nicht mitgelöscht wird. Gibt `false` zurück, wenn noch etwas offen war
   * (dann bleibt alles unverändert).
   */
  async wipeIfEmpty(): Promise<boolean> {
    return transact(this.db, ['outbox', 'meta'], 'readwrite', async ({ outbox, meta }) => {
      if ((await req(outbox.count())) > 0) return false;
      await req(outbox.clear());
      await req(meta.clear());
      return true;
    });
  }
}
